import { getApiBase } from './api-base';
// Resolve at module init — both browser (relative `/api`) and Capacitor
// (absolute https://my360.site/api). Static so calls don't re-check on
// every request, but module is re-evaluated per page load anyway.
const API_BASE = getApiBase();

interface ApiOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: unknown;
  headers?: Record<string, string>;
}

interface JwtPayload {
  exp?: number;
  userId?: string;
  [k: string]: unknown;
}

function decodeJwt(token: string): JwtPayload | null {
  try {
    const [, payload] = token.split('.');
    if (!payload) return null;
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(json) as JwtPayload;
  } catch {
    return null;
  }
}

class ApiClient {
  private accessToken: string | null = null;
  private refreshToken: string | null = null;
  // In-flight refresh promise — guarantees only ONE refresh request fires
  // even if dozens of parallel API calls all hit 401 at the same moment.
  private refreshInFlight: Promise<boolean> | null = null;
  // Proactive refresh timer — fires shortly before access token expires.
  private proactiveTimer: ReturnType<typeof setTimeout> | null = null;
  // Listeners notified when auth state hard-fails (refresh exhausted).
  private authFailListeners = new Set<() => void>();

  constructor() {
    if (typeof window !== 'undefined') {
      this.accessToken = localStorage.getItem('accessToken');
      this.refreshToken = localStorage.getItem('refreshToken');
      this.scheduleProactiveRefresh();
    }
  }

  setTokens(accessToken: string, refreshToken: string) {
    this.accessToken = accessToken;
    this.refreshToken = refreshToken;
    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('refreshToken', refreshToken);
    this.scheduleProactiveRefresh();
  }

  clearTokens() {
    this.accessToken = null;
    this.refreshToken = null;
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    if (this.proactiveTimer) {
      clearTimeout(this.proactiveTimer);
      this.proactiveTimer = null;
    }
  }

  isAuthenticated() {
    return !!this.accessToken;
  }

  /** Subscribe to "session ended" event so the app can redirect/toast. */
  onAuthFail(fn: () => void): () => void {
    this.authFailListeners.add(fn);
    return () => this.authFailListeners.delete(fn);
  }

  private notifyAuthFail() {
    this.authFailListeners.forEach((fn) => {
      try { fn(); } catch { /* swallow */ }
    });
  }

  /** Schedule a refresh ~60s BEFORE the current access token expires.
      Cuts down on user-visible 401s and cache misses. */
  private scheduleProactiveRefresh() {
    if (this.proactiveTimer) {
      clearTimeout(this.proactiveTimer);
      this.proactiveTimer = null;
    }
    if (!this.accessToken || !this.refreshToken) return;

    const payload = decodeJwt(this.accessToken);
    if (!payload?.exp) return;

    const expMs = payload.exp * 1000;
    const now = Date.now();
    // Refresh 60s before expiry, with at least 5s delay
    const refreshIn = Math.max(5_000, expMs - now - 60_000);

    this.proactiveTimer = setTimeout(() => {
      // Fire and forget — refresh updates tokens + reschedules itself
      this.refreshAccessToken().catch(() => { /* refreshAccessToken handles failures */ });
    }, refreshIn);
  }

  private async refreshAccessToken(): Promise<boolean> {
    // Coalesce parallel refresh attempts onto a single in-flight promise
    if (this.refreshInFlight) return this.refreshInFlight;
    if (!this.refreshToken) return false;

    this.refreshInFlight = (async () => {
      try {
        const response = await fetch(`${API_BASE}/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken: this.refreshToken }),
        });

        if (!response.ok) {
          this.clearTokens();
          this.notifyAuthFail();
          return false;
        }

        const data = await response.json();
        if (data.success && data.data?.accessToken) {
          this.setTokens(data.data.accessToken, data.data.refreshToken);
          return true;
        }

        this.clearTokens();
        this.notifyAuthFail();
        return false;
      } catch {
        this.clearTokens();
        this.notifyAuthFail();
        return false;
      } finally {
        this.refreshInFlight = null;
      }
    })();

    return this.refreshInFlight;
  }

  async request<T>(endpoint: string, options: ApiOptions = {}): Promise<T> {
    const { method = 'GET', body, headers = {} } = options;

    const requestHeaders: Record<string, string> = {
      'Content-Type': 'application/json',
      ...headers,
    };

    if (this.accessToken) {
      requestHeaders['Authorization'] = `Bearer ${this.accessToken}`;
    }

    let response = await fetch(`${API_BASE}${endpoint}`, {
      method,
      headers: requestHeaders,
      body: body ? JSON.stringify(body) : undefined,
    });

    // Reactive refresh on 401: parallel callers all wait on the same in-flight refresh
    if (response.status === 401 && this.refreshToken) {
      const refreshed = await this.refreshAccessToken();
      if (refreshed && this.accessToken) {
        requestHeaders['Authorization'] = `Bearer ${this.accessToken}`;
        response = await fetch(`${API_BASE}${endpoint}`, {
          method,
          headers: requestHeaders,
          body: body ? JSON.stringify(body) : undefined,
        });
      }
    }

    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      if (response.status === 401) {
        // After refresh attempt, still 401 → notify listeners (typically Auth store)
        this.notifyAuthFail();
      }
      throw new Error(data?.error || `Request failed (${response.status})`);
    }

    return data;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async post<T = any>(endpoint: string, body?: unknown): Promise<T> {
    return this.request<T>(endpoint, { method: 'POST', body });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async get<T = any>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET' });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async patch<T = any>(endpoint: string, body?: unknown): Promise<T> {
    return this.request<T>(endpoint, { method: 'PATCH', body });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async delete<T = any>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }

  /**
   * Raw fetch with auth headers — used for non-JSON responses (CSV
   * download, file blobs). Skips JSON parsing + ok-throwing so callers
   * can handle response.blob() / response.text() directly.
   */
  async requestRaw(endpoint: string, options: ApiOptions = {}): Promise<Response> {
    const { method = 'GET', body, headers = {} } = options;
    const requestHeaders: Record<string, string> = { ...headers };
    if (this.accessToken) requestHeaders['Authorization'] = `Bearer ${this.accessToken}`;
    if (body && !requestHeaders['Content-Type']) requestHeaders['Content-Type'] = 'application/json';
    let response = await fetch(`${API_BASE}${endpoint}`, {
      method,
      headers: requestHeaders,
      body: body ? JSON.stringify(body) : undefined,
    });
    if (response.status === 401 && this.refreshToken) {
      const refreshed = await this.refreshAccessToken();
      if (refreshed && this.accessToken) {
        requestHeaders['Authorization'] = `Bearer ${this.accessToken}`;
        response = await fetch(`${API_BASE}${endpoint}`, {
          method,
          headers: requestHeaders,
          body: body ? JSON.stringify(body) : undefined,
        });
      }
    }
    return response;
  }

  // Upload helpers live in lib/upload.ts — they call /upload/sign +
  // /upload/finalize directly so the server never proxies file bytes.
}

export const api = new ApiClient();
